/**
 * Theme store — light / dark / system, persisted to localStorage.
 *
 * Architecture notes:
 *   - The user's *preference* is one of 'light' | 'dark' | 'system'.
 *   - The *resolved* theme (what's actually painted) is always 'light' | 'dark'.
 *     When the preference is 'system', resolved follows
 *     `prefers-color-scheme: dark`.
 *   - `class="dark"` on <html> is the canonical signal Tailwind reads for
 *     `dark:` variants. We mutate it from this module so both the React
 *     tree and any CSS-only surfaces (e.g. native scrollbars in styles.css)
 *     stay in sync.
 *   - The same key is read by a tiny inline script in index.html *before*
 *     React boots, to prevent a white flash on reload for dark-mode users.
 *     If you ever change the key, change it in index.html too.
 *
 * Public API:
 *   useThemeStore()                  — Zustand store
 *   useTheme()                       — { theme, resolvedTheme, setTheme, toggle }
 *   initTheme()                      — call once at app start (in main.tsx)
 *
 * `toggle` cycles the user-visible button between explicit Light <-> Dark.
 * The 'system' preference is reachable via setTheme('system') — there's no
 * UI for it yet; the inline boot script plus a fresh-install default of
 * 'system' covers the "just match my OS" use-case for first-time visitors.
 */
import { useEffect } from 'react';
import { create } from 'zustand';
import { persist } from 'zustand/middleware';
const STORAGE_KEY = 'styxdesk.theme';
export const useThemeStore = create()(persist((set, get) => ({
    // Default is 'system' — matches the first-paint script in index.html.
    theme: 'system',
    setTheme: (theme) => {
        set({ theme });
        applyTheme(theme);
    },
    toggle: () => {
        // From any state, the toggle button flips to the opposite explicit choice.
        // 'system' resolves to its current rendered value first.
        const current = resolveTheme(get().theme);
        const next = current === 'dark' ? 'light' : 'dark';
        set({ theme: next });
        applyTheme(next);
    },
}), { name: STORAGE_KEY }));
/** What's currently painted, regardless of the user's preference. */
export function resolveTheme(pref) {
    if (pref === 'light')
        return 'light';
    if (pref === 'dark')
        return 'dark';
    // 'system'
    const sys = typeof window !== 'undefined'
        && window.matchMedia
        && window.matchMedia('(prefers-color-scheme: dark)').matches;
    return sys ? 'dark' : 'light';
}
/** Mutate <html class="dark"> to match a preference. */
function applyTheme(pref) {
    const resolved = resolveTheme(pref);
    const root = document.documentElement;
    if (resolved === 'dark')
        root.classList.add('dark');
    else
        root.classList.remove('dark');
}
/**
 * One-time bootstrap — call from main.tsx so the persisted theme is
 * applied on every page load. The pre-React inline script in index.html
 * also does this, but only at first paint; this call covers the React
 * lifecycle and re-applies after Zustand has rehydrated from storage.
 */
export function initTheme() {
    // Apply once at startup using the rehydrated value.
    applyTheme(useThemeStore.getState().theme);
    // If the user prefers 'system', follow OS theme changes live.
    if (typeof window !== 'undefined' && window.matchMedia) {
        const mql = window.matchMedia('(prefers-color-scheme: dark)');
        const onChange = () => {
            if (useThemeStore.getState().theme === 'system') {
                applyTheme('system');
            }
        };
        // Guard against older browsers that only have addListener.
        if (mql.addEventListener)
            mql.addEventListener('change', onChange);
        else
            mql.addListener(onChange);
    }
}
/**
 * React hook — returns the user's preference + the resolved theme so
 * components can pick assets based on what's actually being painted.
 */
export function useTheme() {
    const theme = useThemeStore((s) => s.theme);
    const setTheme = useThemeStore((s) => s.setTheme);
    const toggle = useThemeStore((s) => s.toggle);
    // Re-render this consumer when the OS theme flips while preference is 'system'.
    useEffect(() => {
        if (theme !== 'system')
            return;
        if (!window.matchMedia)
            return;
        const mql = window.matchMedia('(prefers-color-scheme: dark)');
        const handler = () => {
            // Trigger re-render by re-setting to 'system' (no-op for state value
            // but causes consumers using resolveTheme to see the new resolution).
            useThemeStore.setState({ theme: 'system' });
            applyTheme('system');
        };
        if (mql.addEventListener)
            mql.addEventListener('change', handler);
        else
            mql.addListener(handler);
        return () => {
            if (mql.removeEventListener)
                mql.removeEventListener('change', handler);
            else
                mql.removeListener(handler);
        };
    }, [theme]);
    return {
        theme,
        resolvedTheme: resolveTheme(theme),
        setTheme,
        toggle,
    };
}
